/* ═══════════════════════════════════════════════════════════════
   widgetSources — the data-source catalog for the widget builder
   (Project Center, T5 backend). Each source names a table, the fields a
   filter/measure can target, the view permission(s) that gate reading it,
   and how to actually load rows for one project. `load` goes through the
   SAME cloudCache-backed services every other page uses — never a new
   route.

   ⚠ Deviation from the T5 brief: this file lives under
   `src/renderer/lib/`, not `src/shared/` as the brief specified. Reason:
   `load()` must call renderer services (cloudCache, TaskService, etc),
   which are only visible to `tsconfig.renderer.json`. `tsconfig.main.json`
   compiles `src/shared/**` for the Electron main process WITHOUT
   `src/renderer` in its `include` — a shared/ file importing renderer
   services breaks `npm run build:main` ("file not under rootDir"). The
   pure TYPES (`CustomWidgetDefinition`, `CustomWidget`) stayed in
   `src/shared/entities.ts` per the brief since main/server code may
   reasonably reference the shape; the catalog with its loaders did not.

   `requires` lists ALL of a category's view keys that grant read access —
   requiresAny semantics (holding any ONE is enough), mirroring how
   checkTableAction treats view/view_own/view_team/view_all tiers.
   ═══════════════════════════════════════════════════════════════ */

import type {
  Deliverable,
  ProjectPhase,
  ProjectAssignment,
  HourEntry,
  Invoice,
  Task,
  TaskProjectLink,
  ProjectSpec,
  Expense,
  CustomWidgetDefinition,
} from '../../shared/types';
import { TimeService } from '../services/timeService';
import { TeamService } from '../services/teamService';
import { InvoiceService } from '../services/invoiceService';
import { ProjectSpecService } from '../services/projectSpecService';
import { loadTasksBundle } from '../pages/tasks/taskDataLoader';
import { taskBelongsToProject } from '../pages/tasks/taskPermissions';
import type { DashboardDrill } from '../pages/projects/widgets/context';
import type { DeepLink } from '../types/navigation';

export type WidgetFieldKind = 'text' | 'multiselect' | 'number' | 'date';

export interface WidgetSourceField {
  key: string;
  label: string;
  kind: WidgetFieldKind;
  /** Numeric measures (sum/avg) may target this field. */
  numeric?: boolean;
  /** A $ or rate column — using it in a widget adds the source's financial
   *  gate (see `requiresFor`) on top of its base view requirement. */
  financial?: boolean;
}

/** Informational only (not consumed mechanically by `load`, which already
 *  knows its own project-scoping story) — documents HOW each source is
 *  tied to a project, for the builder UI and for anyone auditing scope. */
export type WidgetSourceScope = { col: string } | { via: 'task_project_links' };

/** Builder R2 — what "complete" means for THIS source, so the generic
 *  `overdue` filter op (filterModel: "before today", source-agnostic) can
 *  be refined to "before today AND not complete" without filterModel ever
 *  needing table-specific domain knowledge. Sources with no natural
 *  due/complete concept (specs, time_entries, team_assignments, expenses)
 *  omit this — `overdue` on them degrades to the date-only check. */
export interface WidgetCompleteField {
  field: string;
  values: string[];
}

/** Builder R2 — where a row (List) or a bucket (Bar/Line) drills to. The
 *  UI wiring (consuming the returned link) is T4b; this is the per-source
 *  data + mapping only. A source with no natural drill target (Expenses)
 *  omits this entirely — its rows/buckets open nothing. */
export interface WidgetDrill {
  row?: (row: Record<string, unknown>) => DashboardDrill | DeepLink | null;
  bucket?: (key: string) => DashboardDrill | DeepLink | null;
}

export interface WidgetSource {
  id: string;
  label: string;
  table: string;
  scope: WidgetSourceScope;
  fields: WidgetSourceField[];
  /** requiresAny — holding ANY one of these view keys grants read access. */
  requires: string[];
  /** The financial gate this source's `financial` fields sit behind. */
  financialKey?: string;
  /** See `WidgetCompleteField`. */
  completeField?: WidgetCompleteField;
  /** See `WidgetDrill`. */
  drill?: WidgetDrill;
  load: (projectId: string) => Promise<Record<string, unknown>[]>;
}

/** Pure — is this row "complete" per its source's mapping? `false` when the
 *  source has no completeness concept (nothing to refine `overdue` with). */
export function isRowComplete(row: Record<string, unknown>, source: WidgetSource): boolean {
  const cf = source.completeField;
  if (!cf) return false;
  const v = row[cf.field];
  return v != null && cf.values.includes(String(v));
}

const asRows = <T>(rows: T[]): Record<string, unknown>[] =>
  rows as unknown as Record<string, unknown>[];

/** Monday-start week key (yyyy-mm-dd) for a date string — used by the
 *  time_entries drill to land on the right Timesheets week. Deliberately
 *  standalone (not imported from widgetEvaluator, which imports FROM this
 *  file — an import back would cycle). */
function weekStartISO(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
  const y = monday.getFullYear();
  const m = String(monday.getMonth() + 1).padStart(2, '0');
  const dd = String(monday.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export const WIDGET_SOURCES: WidgetSource[] = [
  {
    id: 'deliverables',
    label: 'Deliverables',
    table: 'deliverables',
    scope: { col: 'projectId' },
    requires: ['projects.view_own', 'projects.view_all'],
    fields: [
      { key: 'name', label: 'Name', kind: 'text' },
      { key: 'status', label: 'Status', kind: 'multiselect' },
      { key: 'dueDate', label: 'Due Date', kind: 'date' },
      { key: 'sealDate', label: 'Seal Date', kind: 'date' },
      { key: 'issued', label: 'Issued', kind: 'multiselect' },
    ],
    completeField: { field: 'status', values: ['complete'] },
    drill: {
      row: (row) => ({ kind: 'deliverable', deliverableId: String(row.id) }),
    },
    load: async (projectId: string) =>
      asRows<Deliverable>(await TimeService.listDeliverables(projectId)),
  },
  {
    id: 'tasks',
    label: 'Tasks',
    table: 'tasks',
    scope: { via: 'task_project_links' },
    requires: ['tasks.view_own', 'tasks.view_team', 'tasks.view_all'],
    fields: [
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'status', label: 'Status', kind: 'multiselect' },
      { key: 'priority', label: 'Priority', kind: 'multiselect' },
      { key: 'dueDate', label: 'Due Date', kind: 'date' },
      { key: 'tags', label: 'Tags', kind: 'multiselect' },
    ],
    completeField: { field: 'status', values: ['done'] },
    drill: {
      row: (row) => ({ kind: 'task', taskId: String(row.id) }),
    },
    load: async (projectId: string) => {
      // Decision 24: ONE shared loader, same as the Tasks/Attention
      // widgets and useTasksData.
      const { tasks, links } = await loadTasksBundle();
      const scoped: Task[] = tasks.filter((t: Task) =>
        taskBelongsToProject(t, links as TaskProjectLink[], projectId)
      );
      return asRows<Task>(scoped);
    },
  },
  {
    id: 'specs',
    label: 'Specs',
    table: 'project_specs',
    scope: { col: 'projectId' },
    requires: ['project_specs.view'],
    fields: [
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'csiNumber', label: 'CSI Number', kind: 'text' },
      { key: 'compiledAt', label: 'Compiled', kind: 'date' },
      { key: 'archivedAt', label: 'Archived', kind: 'date' },
    ],
    drill: {
      // 'spec' is a new DashboardDrill kind (context.ts) — the union had no
      // entry for opening a project spec; the stock Specs widget uses a
      // separate `ctx.onOpenProjectSpec` callback instead of DashboardDrill,
      // which a generic custom widget has no access to.
      row: (row) => ({ kind: 'spec', specId: String(row.id) }),
    },
    load: async (projectId: string) =>
      asRows<ProjectSpec>(await ProjectSpecService.listByProject(projectId)),
  },
  {
    id: 'time_entries',
    label: 'Time Entries',
    table: 'hour_entries',
    scope: { col: 'projectId' },
    requires: ['timesheets.view_own', 'timesheets.view_team', 'timesheets.view_all'],
    fields: [
      { key: 'date', label: 'Date', kind: 'date' },
      { key: 'hours', label: 'Hours', kind: 'number', numeric: true },
      { key: 'billable', label: 'Billable', kind: 'multiselect' },
      { key: 'note', label: 'Note', kind: 'text' },
    ],
    drill: {
      // Time entries have no per-row editor of their own — the natural
      // target is the Timesheets page filtered to that entry's week.
      row: (row) => ({
        view: 'time-expense',
        tab: 'timesheets',
        projectId: String((row as { projectId?: unknown }).projectId ?? ''),
        weekStart: weekStartISO(String(row.date)),
      }),
      bucket: (key) => ({ view: 'time-expense', tab: 'timesheets', weekStart: key }),
    },
    load: async (projectId: string) => asRows<HourEntry>(await TimeService.listEntries(projectId)),
  },
  {
    id: 'invoices',
    label: 'Invoices',
    table: 'invoices',
    scope: { col: 'projectId' },
    requires: ['invoices.view_own', 'invoices.view_all'],
    financialKey: 'invoices.view_financials',
    fields: [
      { key: 'invoiceNumber', label: 'Invoice #', kind: 'text' },
      { key: 'status', label: 'Status', kind: 'multiselect' },
      { key: 'issueDate', label: 'Issue Date', kind: 'date' },
      { key: 'dueDate', label: 'Due Date', kind: 'date' },
      { key: 'subtotal', label: 'Subtotal', kind: 'number', numeric: true, financial: true },
      { key: 'total', label: 'Total', kind: 'number', numeric: true, financial: true },
      { key: 'amountPaid', label: 'Amount Paid', kind: 'number', numeric: true, financial: true },
    ],
    completeField: { field: 'status', values: ['paid'] },
    drill: {
      // Cross-page — invoices live on the Billing screen, not a project
      // dashboard takeover.
      row: (row) => ({
        view: 'invoices',
        projectId: String((row as { projectId?: unknown }).projectId ?? ''),
        invoiceId: String(row.id),
      }),
    },
    load: async (projectId: string) => asRows<Invoice>(await InvoiceService.list(projectId)),
  },
  {
    id: 'team_assignments',
    label: 'Team Assignments',
    table: 'project_assignments',
    scope: { col: 'projectId' },
    requires: ['projects.view_own', 'projects.view_all'],
    fields: [
      { key: 'teamMemberId', label: 'Team Member', kind: 'multiselect' },
      { key: 'role', label: 'Role', kind: 'text' },
      { key: 'createdAt', label: 'Assigned', kind: 'date' },
    ],
    // No row drill (Team Widget R2, Josh 2026-10-02): "People is for admin
    // stuff, Projects is for project stuff" — a custom widget on this
    // source no longer jumps a row to the People page. Rows on this
    // source are non-clickable, same as any source without a `drill`.
    load: async (projectId: string) =>
      asRows<ProjectAssignment>(await TeamService.listAssignments(undefined, projectId)),
  },
  {
    id: 'phases',
    label: 'Phases',
    table: 'project_phases',
    scope: { col: 'projectId' },
    requires: ['projects.view_own', 'projects.view_all'],
    fields: [
      { key: 'name', label: 'Name', kind: 'text' },
      { key: 'status', label: 'Status', kind: 'multiselect' },
      { key: 'startDate', label: 'Start Date', kind: 'date' },
      { key: 'endDate', label: 'End Date', kind: 'date' },
    ],
    completeField: { field: 'status', values: ['complete'] },
    drill: {
      row: (row) => ({ kind: 'phase', phaseId: String(row.id) }),
    },
    load: async (projectId: string) =>
      asRows<ProjectPhase>(await TimeService.listPhases(projectId)),
  },
  {
    // Builder R2 — 8th source. No natural due-date/complete concept and no
    // drill target (stated per the plan): an expense report has no editor
    // of its own to jump to from a dashboard widget.
    id: 'expenses',
    label: 'Expenses',
    table: 'expenses',
    scope: { col: 'projectId' },
    requires: ['expenses.view_own', 'expenses.view_team', 'expenses.view_all'],
    fields: [
      { key: 'total', label: 'Amount', kind: 'number', numeric: true },
      { key: 'expenseTypeId', label: 'Category', kind: 'multiselect' },
      { key: 'date', label: 'Date', kind: 'date' },
      { key: 'status', label: 'Status', kind: 'multiselect' },
      { key: 'teamMemberId', label: 'Submitted By', kind: 'multiselect' },
    ],
    load: async (projectId: string) => {
      // Lazy import: expenseService.ts captures `window.api` at MODULE LOAD
      // (a pre-existing renderer-only pattern, comment at its top) — a
      // static import here would drag that eager read into every consumer
      // of this file, including widgetEvaluator's pure Node unit tests
      // (ReferenceError: window is not defined). Deferred to call time,
      // which only ever happens inside the real renderer.
      const { ExpenseService } = await import('../services/expenseService');
      return asRows<Expense>(await ExpenseService.listByProject(projectId));
    },
  },
];

export function getWidgetSource(id: string): WidgetSource | undefined {
  return WIDGET_SOURCES.find((s) => s.id === id);
}

/** Every project-dashboard financial widget rides this ONE key, regardless
 *  of source — the same gate the stock (built-in) financial widgets use. */
const PROJECT_FINANCIAL_KEY = 'projects.view_financials';

/** Permission keys a definition needs: the source's base view requirement
 *  (requiresAny — caller must hold at least one) plus, when the definition
 *  touches a `financial` field (via its filter or its measure/groupBy
 *  field), BOTH the universal `projects.view_financials` gate AND the
 *  source's own financial key where it has one (e.g. invoices.
 *  view_financials) — least privilege stacks both, it never substitutes
 *  one for the other. Pure — no I/O. */
export function requiresFor(def: CustomWidgetDefinition, source: WidgetSource): string[] {
  const keys = new Set<string>(source.requires);
  const financialFieldKeys = new Set(source.fields.filter((f) => f.financial).map((f) => f.key));
  if (financialFieldKeys.size === 0) return Array.from(keys);

  const touchesFinancial = (() => {
    const filterFields = def.filter.rules.flatMap((r) =>
      'rules' in r ? r.rules.map((rr) => rr.field) : [r.field]
    );
    if (filterFields.some((f) => financialFieldKeys.has(f))) return true;
    if (def.form.kind === 'number' && typeof def.form.measure === 'object') {
      return financialFieldKeys.has(def.form.measure.field);
    }
    if (def.form.kind === 'bar') {
      if (financialFieldKeys.has(def.form.groupBy)) return true;
      if (typeof def.form.measure === 'object')
        return financialFieldKeys.has(def.form.measure.field);
    }
    if (def.form.kind === 'list') {
      return def.form.fields.some((f) => financialFieldKeys.has(f));
    }
    if (def.form.kind === 'meter') {
      if (typeof def.form.measure === 'object' && financialFieldKeys.has(def.form.measure.field))
        return true;
      return def.form.target.kind === 'field' && financialFieldKeys.has(def.form.target.field);
    }
    if (def.form.kind === 'line') {
      if (financialFieldKeys.has(def.form.dateField)) return true;
      return typeof def.form.measure === 'object' && financialFieldKeys.has(def.form.measure.field);
    }
    return false;
  })();

  if (touchesFinancial) {
    keys.add(PROJECT_FINANCIAL_KEY);
    if (source.financialKey) keys.add(source.financialKey);
  }
  return Array.from(keys);
}
